<script setup lang="ts">
/**
 * 工单 in the navbar, as Aliyun's 支持 is: under the pointer (NavMenu), the
 * tickets by where they stand on a grey band, each to the list filtered so,
 * then filing one, the list and 我的投诉. The counts are the ticket store's,
 * read again as it opens.
 */
import { computed } from 'vue'
import { useRouter, type RouteLocationRaw } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { navPanel } from '@/utils/layout'
import { ticketSummary, refreshTickets } from '@/store/tickets'
import NavMenu from './NavMenu.vue'

const { t } = useI18n()
const router = useRouter()

const stats = computed(() => [
  { status: 'active', label: 'tickets.filter_active', n: ticketSummary.value?.active },
  { status: 'replied', label: 'tickets.filter_replied', n: ticketSummary.value?.replied },
  { status: 'closed', label: 'tickets.filter_closed', n: ticketSummary.value?.closed },
])

const rows: { label: string; to: RouteLocationRaw }[] = [
  { label: 'tickets.newTitle', to: { name: 'tickets', query: { new: '1' } } },
  { label: 'tickets.title', to: { name: 'tickets' } },
  { label: 'complaints.title', to: { name: 'complaints' } },
]

function go(to: RouteLocationRaw) {
  navPanel.value = null
  router.push(to)
}
</script>

<template>
  <NavMenu name="tickets" route="tickets" :label="t('navbar.tickets')" @open="refreshTickets">
    <div class="nav-panel menu-panel">
      <div class="stats">
        <button
          v-for="s in stats"
          :key="s.status"
          type="button"
          class="stat"
          @click="go({ name: 'tickets', query: { status: s.status } })"
        >
          <span class="stat-label">{{ t(s.label) }}</span>
          <span class="stat-n">{{ s.n ?? '—' }}</span>
        </button>
      </div>
      <div class="nav-panel-group">
        <button v-for="r in rows" :key="r.label" type="button" class="nav-panel-row" @click="go(r.to)">
          {{ t(r.label) }}
        </button>
      </div>
    </div>
  </NavMenu>
</template>

<style scoped>
/* Aliyun's 支持: as wide as its counts need, never narrower than 260. */
.menu-panel {
  width: max-content;
  min-width: 260px;
}
/* The counts on Arco's lightest grey, a short rule between them. */
.stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  padding: 16px 20px;
  background: var(--color-fill-1);
}
.stat {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
}
/* Equal columns, each count clear of the rule after it. */
.stat:not(:last-child) {
  padding-right: 12px;
}
.stat + .stat {
  padding-left: 14px;
  border-left: 1px solid var(--nav-rule);
}
.stat-label {
  color: var(--color-text-3);
  font-size: 14px;
  line-height: 22px;
  transition: color 0.1s;
}
.stat-n {
  color: var(--color-text-1);
  font-size: 18px;
  font-weight: 600;
  line-height: 26px;
  font-variant-numeric: tabular-nums;
  transition: color 0.1s;
}
.stat:hover .stat-label,
.stat:hover .stat-n {
  color: rgb(var(--primary-6));
}
/* Straight under the band, its grey is the break. */
.stats + .nav-panel-group {
  border-top: none;
}
</style>
